import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown, ArrowRight } from "lucide-react";
import { COUNTRY_BY_CODE } from "@/lib/countries";

const TH = "px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap text-left";
const TD = "px-4 py-3 align-middle";
const countryName = (c) => COUNTRY_BY_CODE[c.country_code]?.name || "";
const SORTS = {
  name: (c) => c.name.toLowerCase(),
  country: (c) => countryName(c).toLowerCase(),
  accounts: (c) => c.accounts.length,
  jobs: (c) => c.job_count || 0,
};

function Head({ k, label, sort, onSort, right }) {
  const on = sort.k === k;
  const Icon = !on ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`${TH} ${right ? "text-right" : ""}`} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button data-testid={`client-sort-${k}`} onClick={() => onSort(k)} className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${on ? "text-indigo-600" : "hover:text-slate-800"}`}>
        {label}<Icon className={`w-3 h-3 ${on ? "" : "opacity-40"}`} />
      </button>
    </th>
  );
}

export default function ClientsList({ clients, canEdit, onEdit, onDelete }) {
  const nav = useNavigate();
  const [sort, setSort] = useState({ k: "name", dir: "asc" });
  const onSort = (k) => setSort((s) => ({ k, dir: s.k === k && s.dir === "asc" ? "desc" : "asc" }));
  const rows = useMemo(() => {
    const f = SORTS[sort.k], m = sort.dir === "asc" ? 1 : -1;
    return [...clients].sort((a, b) => { const x = f(a), y = f(b); return x < y ? -m : x > y ? m : 0; });
  }, [clients, sort]);
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };

  return (
    <div className="tp-card overflow-x-auto" data-testid="clients-list">
      <table className="w-full text-sm min-w-[640px]">
        <thead className="bg-slate-50 border-b border-[color:var(--bd-sub)]">
          <tr>
            <Head k="name" label="Client" sort={sort} onSort={onSort} />
            <Head k="country" label="Country" sort={sort} onSort={onSort} />
            <Head k="accounts" label="Accounts" sort={sort} onSort={onSort} />
            <Head k="jobs" label="Jobs" sort={sort} onSort={onSort} right />
            <th className={`${TH} w-40`} />
          </tr>
        </thead>
        <tbody className="divide-y divide-[color:var(--bd-sub)]">
          {rows.map((c) => {
            const country = COUNTRY_BY_CODE[c.country_code];
            return (
              <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => nav(`/app/clients/${c.id}`)} className="group cursor-pointer transition-colors hover:bg-slate-50">
                <td className={TD}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center font-display font-semibold shrink-0">{c.name[0]?.toUpperCase()}</div>
                    <span className="font-medium text-slate-900 truncate" data-testid={`client-row-name-${c.id}`}>{c.name}</span>
                  </div>
                </td>
                <td className={`${TD} text-slate-600 whitespace-nowrap`}>{country ? `${country.flag || ""} ${country.name}` : "—"}</td>
                <td className={TD}>
                  <div className="flex flex-wrap gap-1.5 max-w-[360px]" data-testid={`client-row-accounts-${c.id}`}>
                    {c.accounts.length === 0 && <span className="text-xs text-slate-400">None</span>}
                    {c.accounts.slice(0, 3).map((a) => <span key={a.id} className="whitespace-nowrap text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700">{a.name}</span>)}
                    {c.accounts.length > 3 && <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-500">+{c.accounts.length - 3}</span>}
                  </div>
                </td>
                <td className={`${TD} text-right font-semibold text-slate-900`} data-testid={`client-row-jobs-${c.id}`}>{c.job_count || 0}</td>
                <td className={TD}>
                  <div className="flex items-center justify-end gap-1">
                    <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-indigo-600 mr-2 opacity-0 group-hover:opacity-100 transition-opacity">Dashboard <ArrowRight className="w-3.5 h-3.5" /></span>
                    {canEdit && <button data-testid={`client-row-edit-${c.id}`} title="Edit" onClick={stop(() => onEdit(c))} className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"><Pencil className="w-4 h-4" /></button>}
                    {canEdit && <button data-testid={`client-row-delete-${c.id}`} title="Delete" onClick={stop(() => onDelete(c))} className="p-1.5 rounded text-slate-400 hover:text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
